import { FileSelectionType, SelectionMode, getFilterFromSelection } from '@/hooks/useFileSelection.tsx'
import { Loading, Modal } from 'react-daisyui'
import {
  IconArrowsMove,
  IconCheck,
  IconDotsCircleHorizontal,
  IconDownload,
  IconInfoCircle,
  IconPhotoCheck,
  IconShare,
  IconTrash,
  IconX
} from '@tabler/icons-react'
import { ReactNode, useRef, useState } from 'react'
import {
  CreateSelectionRequest,
  CreateShareRequest,
  SelectionAlbumSelection,
  ShareAlbumSelection,
  useClient
} from '@/stores/useClient.ts'
import { useFilesContext } from '@/contexts/filesContext.tsx'
import { useQueryClient } from '@tanstack/react-query'
import { makeKey } from '@/stores/useFiles.ts'
import { useDrives } from '@/stores/useDrives.ts'
import { useUploadQueue } from '@/stores/useUploadQueue.ts'
import { getURLParamsForFilter, useFileFilter } from '@/contexts/fileFilter.tsx'
import { useTranslation } from 'react-i18next'
import { useAllFilesContext } from '@/contexts/allFilesContext.tsx'
import ShareableInput from '@/components/ShareableInput.tsx'
import { hasDrivePermission, HeyDriveType } from '@/models/drive.ts'
import { useProfile } from '@/stores/useAuth.ts'
import { DriveUserRoles } from '@/models/driveUserRole.ts'
import { Button } from '@/components/Button'
import useMultiDownload from '@/hooks/useMultiDownload.ts'
import BulkMoveModal from '@/components/BulkMoveModal.tsx'
import DownloadQualityOptions from '@/components/Files/DownloadQualityOptions.tsx'
import DownloadProgressBar from '@/components/Files/DownloadProgressBar.tsx'
import { DownloadQuality, resolveDriveDownloadQualities } from '@/models/downloadOptions.ts'
import { getApiErrorCode, getApiErrorStatus } from '@/controller/apiErrors.ts'

export default function FileSelection({ fileSelection, hideAll, hideOptions, options, onCancel }: { fileSelection: FileSelectionType, hideAll?: boolean, hideOptions?: boolean, options?: ReactNode, onCancel?: () => void }) {
  const { currentAlbum: album } = useFilesContext()!
  const { drives, currentDrive } = useDrives()
  const {profile} = useProfile()
  const { isAllSelected, setIsAllSelected, setIsSelecting } = fileSelection
  const selectedFiles = fileSelection.getSelectedAlbumFileIds()
  const currentAlbumAllSelected = isAllSelected(album!.id)
  const hasSelectedAlbums = fileSelection.getSelectedAlbumIds().length > 0
  const areActionsDisabled = !currentAlbumAllSelected && !hasSelectedAlbums && selectedFiles.length <= 0
  const [isDeleting, setIsDeleting] = useState(false)
  const [isMoving, setIsMoving] = useState(false)
  const [isSharing, setIsSharing] = useState(false)
  const [shareLink, setShareLink] = useState<string | null>(null)
  const deleteModalRef = useRef<HTMLDialogElement>(null)
  const downloadModalRef = useRef<HTMLDialogElement>(null)
  const moveModalRef = useRef<HTMLDialogElement>(null)
  const actionsModalRef = useRef<HTMLDialogElement>(null)
  const shareModalRef = useRef<HTMLDialogElement>(null)
  const cancelConfirmRef = useRef<HTMLDialogElement>(null)
  const { deleteFiles, updateFiles, createShare, createSelection } = useClient()
  const queryClient = useQueryClient()
  const { removeUploadedFilesForAlbum } = useUploadQueue()
  const {fileFilter} = useFileFilter()
  const { t } = useTranslation()
  const currentFilesCollection = useFilesContext()
  const allFilesContext = useAllFilesContext()
  const { startDownload, isDownloading, downloadRequest, progress } = useMultiDownload()
  const [downloadSuccess, setDownloadSuccess] = useState(false)
  const [selectionConfirmed, setSelectionConfirmed] = useState(false)
  const imageSelectionConfirmRef = useRef<HTMLDialogElement>(null)
  const [selectionName, setSelectionName] = useState('')
  const [selectionEmail, setSelectionEmail] = useState('')
  const [selectionMessage, setSelectionMessage] = useState('')
  const [isSubmittingSelection, setIsSubmittingSelection] = useState(false)
  const [selectionError, setSelectionError] = useState<string | null>(null)

  function onActionsClick() {
    actionsModalRef.current!.showModal()
  }

  // Cancelling clears the selection. On prof drives with an existing selection
  // we confirm first, because the (persisted) progress is lost. When the parent
  // provides an `onCancel` (Album view), we defer to it so the top toolbar
  // cancel and this one behave identically.
  function handleCancel() {
    if (onCancel) {
      onCancel()
      return
    }
    if (isProf && fileSelection.hasSelection) {
      cancelConfirmRef.current!.showModal()
    } else {
      fileSelection.abortSelection()
    }
  }

  function onCancelConfirm() {
    cancelConfirmRef.current!.close()
    fileSelection.abortSelection()
  }

  function onMoveClick() {
    actionsModalRef.current!.close()
    moveModalRef.current!.showModal()
  }

  function onDeleteClick() {
    actionsModalRef.current!.close()
    deleteModalRef.current!.showModal()
  }

  function onDownloadClick() {
    setDownloadSuccess(false)
    downloadModalRef.current!.showModal()
  }

  function onShareModalOpen() {
    actionsModalRef.current!.close()
    setShareLink(null)
    shareModalRef.current!.showModal()
  }

  async function onMultiDownloadClick(quality: DownloadQuality) {
    const albumIds: string[] = fileSelection.getSelectedAlbumIds()
    const fileIds: string[] = fileSelection.getSelectedAlbumFileIds()

    try {
      await startDownload(currentDrive!.id, albumIds, fileIds, quality, () => {
        setDownloadSuccess(true)
      })
    } catch (e: any) {
      console.log('Error multi download', e)
      // This handler must never throw itself — otherwise the actual error message
      // is lost. `response.data` may be an object (DRF) or a raw string.
      const errorCode = getApiErrorCode(e)
      if (errorCode === 'size_exceeds_limit') {
        alert(t('common.downloadTooLarge') + ' ' + t('common.adminDownloadHint', { size: displaySize(downloadMaxFileSizeLimit) }))
      } else if (getApiErrorStatus(e) === 403) {
        // Not an auth prompt: downloading needs no login, but private albums are
        // only available to those allowed to see them.
        alert(t('common.downloadNoPermission', 'Diese Inhalte sind privat und können nicht heruntergeladen werden.'))
      } else {
        alert(t('common.downloadFailed'))
      }
    }
  }

  async function onShareConfirm() {
    if (isSharing) return

    const selectedAlbumIds = fileSelection.getSelectedAlbumIds()
    const selectedFileIds = fileSelection.getSelectedAlbumFileIds()

    const albums: ShareAlbumSelection[] = []

    // Füge komplett selektierte Alben hinzu
    for (const albumId of selectedAlbumIds) {
      albums.push({
        album_id: albumId,
        include_all_files: true,
      })
    }

    // Füge einzeln selektierte Dateien hinzu, gruppiert nach Album
    if (selectedFileIds.length > 0) {
      // Gruppiere Dateien nach Album
      const filesByAlbum: Record<string, string[]> = {}

      for (const fileId of selectedFileIds) {
        // Versuche Album-ID über AllFilesContext zu finden
        let albumIdForFile = allFilesContext?.getAlbumIdForFile(fileId)

        // Fallback: aktuelles Album
        if (!albumIdForFile) {
          albumIdForFile = album!.id
        }

        // Überspringe wenn Album bereits komplett selektiert ist
        if (selectedAlbumIds.includes(albumIdForFile)) {
          continue
        }

        if (!filesByAlbum[albumIdForFile]) {
          filesByAlbum[albumIdForFile] = []
        }
        filesByAlbum[albumIdForFile].push(fileId)
      }

      // Erstelle ShareAlbumSelection für jedes Album mit einzelnen Dateien
      for (const [albumIdForFiles, fileIds] of Object.entries(filesByAlbum)) {
        albums.push({
          album_id: albumIdForFiles,
          include_all_files: false,
          file_ids: fileIds,
        })
      }
    }

    if (albums.length === 0) {
      return
    }

    const shareRequest: CreateShareRequest = {
      albums,
    }

    setIsSharing(true)
    try {
      const response = await createShare(currentDrive!.id, shareRequest)
      console.log('Share erstellt:', response.data)
      // Erstelle den Share-Link aus der Response
      const shareId = response.data.id
      const shareUrl = `${window.location.origin}/s/${shareId}`
      setShareLink(shareUrl)
    } catch (e: any) {
      console.error('Fehler beim Erstellen des Shares:', e)
      // Sharing needs no login. A 403 is a real refusal: private albums may only
      // be shared by those allowed to see them.
      alert(getApiErrorStatus(e) === 403
        ? t('common.shareNoPermission', 'Diese Inhalte sind privat und können nicht geteilt werden.')
        : 'Fehler beim Erstellen des Shares')
    } finally {
      setIsSharing(false)
    }
  }

  // Berechne die Anzahl der ausgewählten Alben und Dateien für das Share-Modal
  function getShareCounts() {
    const selectedAlbumIds = fileSelection.getSelectedAlbumIds()
    const selectedFileIds = fileSelection.getSelectedAlbumFileIds()

    // Zähle Dateien, die nicht in komplett ausgewählten Alben sind
    let fileCount = 0
    for (const fileId of selectedFileIds) {
      let albumIdForFile = allFilesContext?.getAlbumIdForFile(fileId)
      if (!albumIdForFile) {
        albumIdForFile = album!.id
      }
      // Nur zählen wenn Album nicht komplett ausgewählt ist
      if (!selectedAlbumIds.includes(albumIdForFile)) {
        fileCount++
      }
    }

    return {
      albumCount: selectedAlbumIds.length,
      fileCount
    }
  }

  function openSelectionConfirm() {
    setSelectionConfirmed(false)
    setSelectionEmail('')
    setSelectionMessage('')
    setSelectionError(null)
    setIsSubmittingSelection(false)
    imageSelectionConfirmRef.current?.showModal()
  }

  async function onSelectionSubmit() {
    if (isSubmittingSelection) return

    const selectedAlbumIds = fileSelection.getSelectedAlbumIds()
    const selectedFileIds = fileSelection.getSelectedAlbumFileIds()

    const albums: SelectionAlbumSelection[] = []

    for (const albumId of selectedAlbumIds) {
      albums.push({ album_id: albumId, include_all_files: true })
    }

    if (selectedFileIds.length > 0) {
      const filesByAlbum: Record<string, string[]> = {}
      for (const fileId of selectedFileIds) {
        let albumIdForFile = allFilesContext?.getAlbumIdForFile(fileId)
        if (!albumIdForFile) albumIdForFile = album!.id
        if (selectedAlbumIds.includes(albumIdForFile)) continue
        if (!filesByAlbum[albumIdForFile]) filesByAlbum[albumIdForFile] = []
        filesByAlbum[albumIdForFile].push(fileId)
      }
      for (const [albumIdForFiles, fileIds] of Object.entries(filesByAlbum)) {
        albums.push({ album_id: albumIdForFiles, include_all_files: false, file_ids: fileIds })
      }
    }

    if (albums.length === 0) return

    const request: CreateSelectionRequest = {
      albums,
      ...(selectionName.trim() ? { name: selectionName.trim() } : {}),
      ...(selectionEmail.trim() ? { email: selectionEmail.trim() } : {}),
      ...(selectionMessage.trim() ? { message: selectionMessage.trim() } : {}),
    }

    setIsSubmittingSelection(true)
    setSelectionError(null)
    try {
      await createSelection(currentDrive!.id, request)
      setSelectionConfirmed(true)
    } catch (e: any) {
      console.error('Fehler beim Erstellen der Auswahl:', e)
      setSelectionError('Fehler beim Senden. Bitte erneut versuchen.')
    } finally {
      setIsSubmittingSelection(false)
    }
  }

  async function onDeleteConfirm() {
    if (isDeleting) {
      return
    }

    let filterFromSelection: Array<Array<string>>
    try {
      filterFromSelection = getFilterFromSelection(fileSelection, album!.id)
    } catch (e) {
      alert('Cannot create filter')
      return
    }
    let filterFromFileFilter = fileFilter ? getURLParamsForFilter(fileFilter) : []

    const filter = [...filterFromSelection, ...filterFromFileFilter]

    setIsDeleting(true)
    try {
      await deleteFiles(album!, filter)
      await queryClient.invalidateQueries({
        queryKey: makeKey(album!.id),
      })
      removeUploadedFilesForAlbum(album!)
      setIsSelecting(false)
      deleteModalRef.current!.close()
    } catch (e: any) {
      alert('Error deleting files: ' + JSON.stringify(e.response))
      return
    } finally {

      setIsDeleting(false)
    }
  }

  async function onMoveConfirm(targetMoveAlbum: import('@/models/album.ts').Album) {
    let filterFromSelection: Array<Array<string>>
    try {
      filterFromSelection = getFilterFromSelection(fileSelection, album!.id)
    } catch (e) {
      alert('Cannot create filter')
      return
    }
    let filterFromFileFilter = fileFilter ? getURLParamsForFilter(fileFilter) : []

    const filter = [...filterFromSelection, ...filterFromFileFilter]

    setIsMoving(true)
    try {
      await updateFiles(album!, filter, { album: targetMoveAlbum.id })
      await queryClient.invalidateQueries({
        queryKey: makeKey(album!.id),
      })
      await queryClient.invalidateQueries({
        queryKey: makeKey(targetMoveAlbum.id),
      })
      removeUploadedFilesForAlbum(album!)
      setIsSelecting(false)
      moveModalRef.current!.close()
    } catch (e: any) {
      alert('Error moving files: ' + JSON.stringify(e.response))
      return
    } finally {
      setIsMoving(false)
    }
  }

  let fileSizeHigh: number | null = 0
  let fileSizeOriginal: number | null = 0
  if (fileSelection.getSelectedAlbumIds().length) {
    fileSizeHigh = null
    fileSizeOriginal = null
  } else {
    if (currentFilesCollection?.currentFilesCollection) {
      for (const fileId of fileSelection.getSelectedAlbumFileIds()) {
        const file = currentFilesCollection.currentFilesCollection.files.find(file => file.id == fileId)
        if (file) {
          fileSizeHigh += file.detail_size || file.size
          fileSizeOriginal += file.size
        }
      }
    }
  }

  const downloadMaxFileSizeLimit = 1024 * 1024 * 1024

  function displayErrorIfTooLarge(size: number | null) {
    if (size && size > downloadMaxFileSizeLimit) {
      return <p className='text-sm text-error text-center'>{t('common.downloadOptionDisabled', { size: displaySize(downloadMaxFileSizeLimit) })}</p>
    }
  }

  function displaySize(size: number) {
    size /= 1024
    return size < 250 ? size.toFixed(0) + ' KB' : (size / 1024).toFixed(2) + ' MB'
  }

  const highDisabled = !!fileSizeHigh && fileSizeHigh > downloadMaxFileSizeLimit
  const originalDisabled = !!fileSizeOriginal && fileSizeOriginal > downloadMaxFileSizeLimit
  const qualityDisabled: Partial<Record<DownloadQuality, boolean>> = { high: highDisabled, original: originalDisabled }

  const manyFilesThreshold = 20
  const bigFilesThreshold = 1024 * 1024 * 20
  const isDownloadingManyOrBigFiles = downloadRequest && (downloadRequest.files_count >= manyFilesThreshold || downloadRequest.projected_size > bigFilesThreshold)

  const isAdmin = profile && hasDrivePermission(currentDrive!, profile, DriveUserRoles.ADMIN)
  const isViewer = profile && hasDrivePermission(currentDrive!, profile, DriveUserRoles.FULL_VIEWER)

  const isSpecialMode = fileSelection.selectionMode !== SelectionMode.DEFAULT
  const isProf = currentDrive?.drive_type === HeyDriveType.PROF
  const hideDownload = !!currentDrive?.is_hide_download
  const hideShare = !!currentDrive?.is_hide_share
  const hideSelection = !!currentDrive?.is_hide_selection
  // Which resolutions this drive offers. A watermark still wins over the setting.
  const downloadQualities = resolveDriveDownloadQualities(currentDrive)
  // Only options that are actually offered may trigger the "single files always work" hint.
  const isAnyOfferedQualityDisabled = downloadQualities.some(quality => !!qualityDisabled[quality])

  // Double-check pattern: every entry in the "weitere Aktionen" (DotsCircle) modal
  // has its OWN canX flag. `hasActions` is the OR of all canX flags and controls
  // whether the DotsCircle button itself is rendered. When adding a new action,
  // (1) add its canX flag, (2) include it in `hasActions`, (3) gate its modal
  // entry on canX. Forgetting (2) means the button stays hidden even though an
  // action is available; forgetting (3) means a disabled action shows up inside.
  const canMove = !!(isAdmin && !isProf && currentDrive && drives[currentDrive.id].albums.length > 1)
  const canShare = !!((isAdmin || isViewer || isProf) && !hideShare)
  const canDelete = !!(isAdmin && !isProf)
  const canSelection = !!(isProf && !hideSelection)
  const hasActions = canMove || canShare || canDelete || canSelection

  return <div className='fixed bottom-0 left-0 w-full z-50 bg-secondary border-t-4 border-primary shadow-lg'>
    {/* Default mode: single row */}
    {!isSpecialMode && (
      <div className='container px-4 mx-auto py-3 flex items-center justify-center flex-row h-[4rem]'>
        <div className='flex-1 flex justify-start items-center gap-2 font-semibold leading-5'>
          <button
            type='button'
            title={t('common.cancel')}
            aria-label={t('common.cancel')}
            className='p-1.5 rounded-full text-base-content opacity-50 hover:opacity-100 transition-opacity'
            onClick={handleCancel}
          >
            <IconX size={18} />
          </button>
          {
            !hideAll &&
            <Button
              fullRounded
              className='border-4'
              variant={currentAlbumAllSelected ? undefined : 'outline'}
              color={'primary'}
              size='sm'
              onClick={() => setIsAllSelected(album!.id, !currentAlbumAllSelected)}
            >
              {t('common.all')}
            </Button>
          }
        </div>
        <div className='flex-1 flex justify-center items-center text-center font-semibold leading-5 text-primary'>
          { hideAll
            ? ((hasSelectedAlbums || currentAlbumAllSelected) ? t('common.selectObjects') : selectedFiles.length > 0 ? t('common.selected', {count: selectedFiles.length}) : t('common.selectObjects'))
            : (currentAlbumAllSelected ? t('common.allSelected') : selectedFiles.length > 0 ? t('common.selected', {count: selectedFiles.length}) : t('common.selectObjects'))
          }
        </div>
        <div className='flex-1 flex justify-end items-center text-center font-semibold leading-5 gap-2'>
          {!hideOptions && (
            <>
              {!hideDownload && (
                <Button disabled={areActionsDisabled} className='px-1 border-0' color='primary' variant='outline' onClick={onDownloadClick}>
                  <IconDownload />
                </Button>
              )}
              {hasActions && (
                <Button disabled={areActionsDisabled} className='px-1 border-0' color='primary' variant='outline' onClick={onActionsClick}>
                  <IconDotsCircleHorizontal />
                </Button>
              )}
            </>
          )}
          {options}
        </div>
      </div>
    )}

    {/* Special modes (DOWNLOAD / SHARE / IMAGE_SELECTION): two rows */}
    {isSpecialMode && (
      <div className='container px-4 mx-auto pt-3 pb-2 flex flex-col gap-2'>
        {/* Top row: count info */}
        <div className='flex justify-center items-center text-center font-semibold leading-5 text-primary'>
          { hideAll
            ? ((hasSelectedAlbums || currentAlbumAllSelected) ? t('common.selectObjects') : selectedFiles.length > 0 ? t('common.selected', {count: selectedFiles.length}) : t('common.selectObjects'))
            : (currentAlbumAllSelected ? t('common.allSelected') : selectedFiles.length > 0 ? t('common.selected', {count: selectedFiles.length}) : t('common.selectObjects'))
          }
        </div>
        {/* Bottom row: cancel + action */}
        <div className='flex items-center justify-between gap-2'>
          <button
            type='button'
            title={t('common.cancel')}
            aria-label={t('common.cancel')}
            className='p-1.5 rounded-full text-base-content opacity-50 hover:opacity-100 transition-opacity'
            onClick={handleCancel}
          >
            <IconX size={18} />
          </button>
          {fileSelection.selectionMode === SelectionMode.DOWNLOAD && (
            <Button disabled={areActionsDisabled} color='primary' size='sm' onClick={onDownloadClick}>
              <IconDownload size={18} /> Herunterladen
            </Button>
          )}
          {fileSelection.selectionMode === SelectionMode.SHARE && (
            <Button disabled={areActionsDisabled} color='primary' size='sm' onClick={onShareModalOpen}>
              <IconShare size={18} /> Bilder teilen
            </Button>
          )}
          {fileSelection.selectionMode === SelectionMode.IMAGE_SELECTION && (
            <Button
              disabled={areActionsDisabled}
              color='primary'
              size='sm'
              onClick={openSelectionConfirm}
            >
              <IconCheck size={18} /> Bestätigen
            </Button>
          )}
        </div>
      </div>
    )}
    <Modal ref={deleteModalRef}>
      <Modal.Header>
        <div className='flex flex-row gap-2 justify-center'>
          <div className='bg-primary text-neutral rounded-full p-1'>
            <IconTrash size='20'/>
          </div>
          {t('common.deleteObjects')}
        </div>
        <form method="dialog">
          <p className='mt-3 text-center'>
            { currentAlbumAllSelected && t('common.deleteAllAlbumRly') }
            { !currentAlbumAllSelected && t('common.deleteObjectsRly') }
          </p>
          <button disabled={isDeleting} className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕
          </button>
        </form>
      </Modal.Header>
      <Modal.Body>
        <div className='flex flex-col gap-2 items-center'>
          <Button disabled={isDeleting} loading={isDeleting} className='px-5' color='error' onClick={onDeleteConfirm}>
            {t('common.delete')}
          </Button>
          <form method="dialog">
            <Button disabled={isDeleting} color='ghost'>
              {t('common.cancel')}
            </Button>
          </form>
        </div>
      </Modal.Body>
    </Modal>
    <Modal ref={downloadModalRef}>
      <Modal.Header>
        <div className='flex flex-row gap-2 justify-center'>
          <div className='bg-primary text-neutral rounded-full p-1'>
            <IconDownload size='20'/>
          </div>
          Download
        </div>
        <form method="dialog">
          <button disabled={isDeleting} className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
        </form>
      </Modal.Header>
      <Modal.Body>
        { isDownloading && <div className='flex flex-col gap-2 items-center'>
          <DownloadProgressBar progress={progress} />
          { isDownloadingManyOrBigFiles && <p className='text-center'>
            {t('common.downloadManyFilesPatience')}
          </p>}
        </div>}
        { !isDownloading && downloadSuccess && <div className='flex flex-col gap-4 items-center py-4'>
          <div className='bg-primary text-neutral rounded-full p-3'>
            <IconCheck size={32} stroke={2.5} />
          </div>
          <p className='text-center text-lg font-medium'>Datei gespeichert</p>
          <p className='text-center text-sm opacity-60'>Dein Download wurde erfolgreich erstellt.</p>
          <form method="dialog">
            <Button className='mt-2' color='primary'>
              Fertig
            </Button>
          </form>
        </div>}
        { !isDownloading && !downloadSuccess && <div className='flex flex-col gap-2 items-center'>
          <DownloadQualityOptions
            qualities={downloadQualities}
            onSelect={onMultiDownloadClick}
            disabled={qualityDisabled}
            hint={{
              high: fileSizeHigh ? `${t('common.approx')} ${displaySize(fileSizeHigh)}` : undefined,
              original: fileSizeOriginal ? `${t('common.approx')} ${displaySize(fileSizeOriginal)}` : undefined,
            }}
            note={{
              high: displayErrorIfTooLarge(fileSizeHigh),
              original: displayErrorIfTooLarge(fileSizeOriginal),
            }}
          />
          { isAnyOfferedQualityDisabled && <div className='bg-primary rounded-lg p-5 my-5 text-neutral w-full flex flex-col items-start gap-2'>
            <IconInfoCircle size={26} />
            <p>{t('common.singleFilesDownloadHint')}</p>
            <p>{t('common.adminDownloadHint', { size: displaySize(downloadMaxFileSizeLimit) })}</p>
          </div> }
          <form method="dialog">
            <Button className='mt-5' disabled={isDownloading} loading={isDownloading} color='ghost'>
              {t('common.cancel')}
            </Button>
          </form>
        </div> }
      </Modal.Body>
    </Modal>
    <BulkMoveModal
      modalRef={moveModalRef}
      availableAlbums={drives[currentDrive!.id].albums.filter(a => album!.id !== a.id)}
      onConfirm={onMoveConfirm}
      isLoading={isMoving}
    />
    <Modal ref={actionsModalRef} className='bg-neutral'>
      <Modal.Header>
        <div className='flex flex-row gap-2 justify-center'>
          <div className='bg-primary text-neutral rounded-full p-1'>
            <IconDotsCircleHorizontal size='20'/>
          </div>
          {t('common.actions')}
        </div>
        <form method="dialog">
          <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
        </form>
      </Modal.Header>
      <Modal.Body>
        <div className='flex flex-col gap-3'>
          {isAdmin && !isProf && drives[currentDrive!.id].albums.length > 1 && (
            <Button color='primary' variant='outline' className='w-full justify-start gap-3' onClick={onMoveClick}>
              <IconArrowsMove size={20} />
              {t('common.moveObjects')}
            </Button>
          )}
          {canShare && (
            <Button color='primary' variant='outline' className='w-full justify-start gap-3' onClick={onShareModalOpen}>
              <IconShare size={20} />
              {t('common.share')}
            </Button>
          )}
          {canSelection && (
            <Button color='primary' variant='outline' className='w-full justify-start gap-3' onClick={() => { actionsModalRef.current!.close(); openSelectionConfirm() }}>
              <IconPhotoCheck size={20} />
              Auswahl absenden
            </Button>
          )}
          {isAdmin && !isProf && (
            <>
              <div className='border-t border-base-300 my-2' />
              <Button color='error' variant='outline' className='w-full justify-start gap-3' onClick={onDeleteClick}>
                <IconTrash size={20} />
                {t('common.delete')}
              </Button>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
    <Modal ref={shareModalRef} className='bg-neutral'>
      <Modal.Header>
        <div className='flex flex-col items-center gap-3 pt-2'>
          <div className='bg-primary bg-opacity-20 text-primary rounded-full p-4'>
            <IconShare size={36} />
          </div>
          <span className='text-xl font-bold text-center'>
            {shareLink ? 'Teilen-Link erstellt' : 'Ausgewählte Inhalte teilen'}
          </span>
        </div>
        <form method="dialog">
          <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
        </form>
      </Modal.Header>
      <Modal.Body>
        {!shareLink ? (
          <div className='flex flex-col gap-4 items-center'>
            <div className='text-center'>
              {(() => {
                const { albumCount, fileCount } = getShareCounts()
                return (
                  <div className='flex flex-col gap-1'>
                    {albumCount > 0 && (
                      <p className='text-base-content'>
                        <span className='font-semibold'>{albumCount}</span> {albumCount === 1 ? 'Album' : 'Alben'} ausgewählt
                      </p>
                    )}
                    {fileCount > 0 && (
                      <p className='text-base-content'>
                        <span className='font-semibold'>{fileCount}</span> {fileCount === 1 ? 'Datei' : 'Dateien'} ausgewählt
                      </p>
                    )}
                  </div>
                )
              })()}
            </div>
            <Button color='primary' onClick={onShareConfirm} loading={isSharing} disabled={isSharing}>
              Teilen-Link erstellen
            </Button>
            <form method="dialog">
              <Button color='ghost'>
                {t('common.cancel')}
              </Button>
            </form>
          </div>
        ) : (
          <div className='flex flex-col gap-4 items-center'>
            <p className='text-center text-base-content opacity-80'>
              Teile diesen Link, um anderen Zugriff auf die ausgewählten Inhalte zu geben.
            </p>
            <div className='w-full'>
              <ShareableInput value={shareLink} />
            </div>
            <form method="dialog">
              <Button color='ghost' className='mt-2'>
                {t('common.cancel')}
              </Button>
            </form>
          </div>
        )}
      </Modal.Body>
    </Modal>
    <Modal ref={imageSelectionConfirmRef} className='bg-neutral'>
      <Modal.Header>
        <div className='flex flex-row gap-2 justify-center'>
          <div className='bg-primary text-neutral rounded-full p-1'>
            <IconCheck size={20} />
          </div>
          {selectionConfirmed ? 'Auswahl gesendet' : 'Auswahl absenden'}
        </div>
        <form method='dialog'>
          <button className='btn btn-sm btn-circle btn-ghost absolute right-5 top-5' disabled={isSubmittingSelection}>✕</button>
        </form>
      </Modal.Header>
      <Modal.Body>
        {selectionConfirmed ? (
          <div className='flex flex-col gap-4 items-center py-4'>
            <div className='bg-primary text-neutral rounded-full p-3'>
              <IconCheck size={32} stroke={2.5} />
            </div>
            <p className='text-center text-lg font-medium'>Auswahl gesendet</p>
            <p className='text-center text-sm opacity-60'>
              Der Fotograf wurde über deine Auswahl informiert.
            </p>
            <Button className='mt-2' color='primary' onClick={() => {
              imageSelectionConfirmRef.current?.close()
              setIsSelecting(false)
            }}>
              Fertig
            </Button>
          </div>
        ) : isSubmittingSelection ? (
          <div className='flex flex-col gap-3 items-center py-4'>
            <Loading color='primary' />
            <p className='text-center'>Auswahl wird gesendet...</p>
          </div>
        ) : (
          <form
            className='flex flex-col gap-4'
            onSubmit={e => {
              e.preventDefault()
              onSelectionSubmit()
            }}
          >
            <div className='text-center'>
              {(() => {
                const { albumCount, fileCount } = getShareCounts()
                return (
                  <div className='flex flex-col gap-1'>
                    {albumCount > 0 && (
                      <p>
                        <span className='font-semibold'>{albumCount}</span> {albumCount === 1 ? 'Album' : 'Alben'} ausgewählt
                      </p>
                    )}
                    {fileCount > 0 && (
                      <p>
                        <span className='font-semibold'>{fileCount}</span> {fileCount === 1 ? 'Bild' : 'Bilder'} ausgewählt
                      </p>
                    )}
                  </div>
                )
              })()}
            </div>
            <label className='form-control w-full'>
              <div className='label'>
                <span className='label-text'>Dein Name {currentDrive?.is_selection_name_required ? '' : '(optional)'}</span>
              </div>
              <input
                type='text'
                className='input input-bordered w-full'
                placeholder='Vor- und Nachname'
                value={selectionName}
                onChange={e => setSelectionName(e.target.value)}
                required={!!currentDrive?.is_selection_name_required}
              />
            </label>
            <label className='form-control w-full'>
              <div className='label'>
                <span className='label-text'>Deine Email {currentDrive?.is_selection_email_required ? '' : '(optional)'}</span>
              </div>
              <input
                type='email'
                className='input input-bordered w-full'
                placeholder='name@beispiel.de'
                value={selectionEmail}
                onChange={e => setSelectionEmail(e.target.value)}
                required={!!currentDrive?.is_selection_email_required}
              />
            </label>
            <label className='form-control w-full'>
              <div className='label'>
                <span className='label-text'>Nachricht an den Fotografen {currentDrive?.is_selection_message_required ? '' : '(optional)'}</span>
              </div>
              <textarea
                className='textarea textarea-bordered w-full min-h-24'
                placeholder='Anmerkungen, Wünsche, ...'
                value={selectionMessage}
                onChange={e => setSelectionMessage(e.target.value)}
                required={!!currentDrive?.is_selection_message_required}
              />
            </label>
            {selectionError && (
              <p className='text-error text-sm text-center'>{selectionError}</p>
            )}
            <div className='flex flex-col gap-2 items-center mt-2'>
              <Button
                type='submit'
                color='primary'
                disabled={isSubmittingSelection || areActionsDisabled}
                loading={isSubmittingSelection}
              >
                Auswahl absenden
              </Button>
              <Button
                type='button'
                color='ghost'
                disabled={isSubmittingSelection}
                onClick={() => imageSelectionConfirmRef.current?.close()}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        )}
      </Modal.Body>
    </Modal>
    <Modal ref={cancelConfirmRef} className='bg-neutral'>
      <Modal.Header>
        <div className='flex flex-row gap-2 justify-center'>
          <div className='bg-primary text-neutral rounded-full p-1'>
            <IconInfoCircle size={20} />
          </div>
          Auswahl abbrechen
        </div>
        <form method='dialog'>
          <button className='btn btn-sm btn-circle btn-ghost absolute right-5 top-5'>✕</button>
        </form>
      </Modal.Header>
      <Modal.Body>
        <div className='flex flex-col gap-4 items-center'>
          <p className='text-center'>Möchtest du die Auswahl wirklich abbrechen? Deine bisherige Auswahl geht dabei verloren.</p>
          <div className='flex flex-col gap-2 items-center'>
            <Button color='primary' onClick={onCancelConfirm}>
              Auswahl verwerfen
            </Button>
            <form method='dialog'>
              <Button color='ghost'>
                {t('common.back', 'Zurück')}
              </Button>
            </form>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  </div>
}
